/**
 * Reading the help chat's event stream.
 *
 * `EventSource` cannot be used here: it is GET-only and cannot carry a request body, while the chat
 * is a POST with JSON. So the stream is read from `fetch`'s response body, and frames are decoded
 * here.
 *
 * Chunk boundaries are arbitrary — they split mid-word and mid-frame — so bytes are buffered until
 * a frame completes. Event names are never interpreted here: unknown ones are handed on and
 * ignored by the consumer, which is what lets the API add events without breaking this client.
 */

/** One decoded frame. `data` stays `unknown` — an unrecognised event is data, not a contract. */
export interface SseFrame {
	event: string;
	data: unknown;
}

const FRAME_SEPARATOR = /\r?\n\r?\n/;

/** Decode one frame's `event:` / `data:` lines; comment keep-alives yield nothing. */
function parseFrame(frame: string): SseFrame | null {
	let event = "";
	const dataLines: string[] = [];

	for (const line of frame.split(/\r?\n/)) {
		if (line.startsWith(":")) {
			continue;
		}

		if (line.startsWith("event:")) {
			event = line.slice("event:".length).trim();
		} else if (line.startsWith("data:")) {
			dataLines.push(line.slice("data:".length).trim());
		}
	}

	if (!event) {
		return null;
	}

	try {
		return { event, data: dataLines.length > 0 ? JSON.parse(dataLines.join("\n")) : undefined };
	} catch {
		return null;
	}
}

/** Incremental reader: feed chunks, get back the frames that completed. */
export class SseFrameReader {
	private buffer = "";

	push(chunk: string): SseFrame[] {
		this.buffer += chunk;

		const frames = this.buffer.split(FRAME_SEPARATOR);

		this.buffer = frames.pop() ?? "";

		return frames.flatMap((frame) => parseFrame(frame) ?? []);
	}

	/** Decode a final frame that arrived without its trailing blank line. */
	flush(): SseFrame[] {
		const remainder = this.buffer.trim();

		this.buffer = "";

		const frame = remainder ? parseFrame(remainder) : null;

		return frame ? [frame] : [];
	}
}

/**
 * Copy for a failed turn, keyed by the error code the API returned.
 *
 * The two rate limits are the assistant's, not the user's client being throttled, so they read as
 * "the assistant is busy" rather than "you did something wrong" — and the daily one has to say
 * something different, because retrying it is pointless for hours.
 */
export function helpChatErrorKey(code?: string): string {
	switch (code) {
		case "HELP_ASSISTANT_DAILY_LIMIT":
			return "helpCenter.chat.dailyLimit";
		case "HELP_ASSISTANT_RATE_LIMITED":
			return "helpCenter.chat.busy";
		default:
			return "helpCenter.chat.error";
	}
}
